/**
 * Summer50 Growth admin service — API client for /api/admin/growth/*.
 * Follows adminCampaignService.ts patterns.
 */

import { storage } from './storage';
import { API_BASE_URL } from '../utils/api';
import { AdminApiError, adminRequest } from '../utils/adminApi';
import { detailText } from '../pages/admin/partners/partnersLogic';

/** The server's words for a refused request, from its JSON body — FastAPI's 422 list
 *  flattened into one line (review N6) — or `fallback` when the body says nothing usable. */
export function refusalMessage(body: unknown, fallback: string): string {
  const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : undefined;
  return detailText(detail) ?? fallback;
}

export class Summer50GrowthService {
  private static readonly API_BASE = `${API_BASE_URL}/api/admin/growth`;

  private static async headers(): Promise<Record<string, string>> {
    const h: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Client': 'firebase-portal',
    };
    const token = await storage.getItem('admin_token');
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }

  private static async req(path: string, method = 'GET', body?: any): Promise<any> {
    const res = await fetch(`${this.API_BASE}${path}`, {
      method,
      headers: await this.headers(),
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let msg = `Request failed (${res.status})`;
      try {
        msg = refusalMessage(await res.json(), msg);
      } catch { /* not JSON: keep the status line */ }
      throw new Error(msg);
    }
    return res.json();
  }

  static dashboard() { return this.req('/dashboard'); }
  static listCodes(params: { status?: string; wave?: string; search?: string; code_type?: string } = {}) {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as any).toString();
    return this.req(`/codes${q ? '?' + q : ''}`);
  }
  static generateCodes(wave: string, count: number) { return this.req('/codes/generate', 'POST', { wave, count }); }
  static createCustom(code: string, wave: string, note: string) { return this.req('/codes/create-custom', 'POST', { code, wave, note }); }
  /** Duration options for the issue-a-code form. Served by the backend so the dropdown
   *  can never offer a value RevenueCat would reject. */
  static durations() { return this.req('/codes/durations'); }
  /** Mint ONE strategic code for a named evaluator/influencer. */
  static createNamed(firstName: string, duration: string, note = '') {
    return this.req('/codes/create-named', 'POST', { first_name: firstName, duration, note });
  }
  static allocate(codeId: string, handleOrNote: string) { return this.req(`/codes/${codeId}/allocate`, 'POST', { handle_or_note: handleOrNote }); }
  static revokeCode(codeId: string) { return this.req(`/codes/${codeId}/revoke`, 'POST'); }
  static extendExpiry(codeId: string, hours: number) { return this.req(`/codes/${codeId}/extend-expiry`, 'POST', { hours }); }
  static reissue(codeId: string) { return this.req(`/codes/${codeId}/reissue`, 'POST'); }
  static codeDrill(codeId: string) { return this.req(`/codes/${codeId}`); }
  static attempts() { return this.req('/attempts'); }
  static rewards() { return this.req('/rewards'); }
  static voidReward(id: string, reason: string) { return this.req(`/rewards/${id}/void`, 'POST', { reason }); }
  static referralGraph() { return this.req('/referrals/graph'); }
  static inventory() { return this.req('/inventory'); }
  static capExempt(userId: string, days: number) { return this.req(`/users/${userId}/cap-exempt`, 'POST', { days }); }
  static revokeFreeMonth(userId: string) { return this.req(`/users/${userId}/revoke-free-month`, 'POST'); }
  /** The codes CSV as a file, through the shared admin request (utils/adminApi — the admin
   *  session on the call; a plain link can't carry the Bearer token, so it always answered
   *  401). Admin roles only: 403 for support and viewer. */
  static async exportCodes(wave?: string): Promise<Blob> {
    try {
      const res = await adminRequest(`/api/admin/growth/codes/export${wave ? '?wave=' + encodeURIComponent(wave) : ''}`);
      return await res.blob();
    } catch (e) {
      if (!(e instanceof AdminApiError)) throw e;
      let msg = `Export failed (${e.status})`;
      try {
        msg = refusalMessage(JSON.parse(e.body), msg);
      } catch { /* not JSON: keep the status line */ }
      throw new Error(msg);
    }
  }
}
